import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  TextField,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { publicApi } from '../api/publicApi';
import { format } from 'date-fns';
import { ProjectWithCount } from '../types/project';
import { Issue } from '../types/issue';
import { getImageUrl } from '../utils/imageUrl';

export default function PublicProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [project, setProject] = useState<ProjectWithCount | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch project and issues
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const projectId = parseInt(id);
        if (isNaN(projectId)) {
          setError('Invalid project ID');
          setLoading(false);
          return;
        }
        
        // Fetch project
        const projectData = await publicApi.getProject(projectId);
        setProject(projectData);
        
        // Fetch issues
        const issuesData = await publicApi.getIssues({ project_id: projectId });
        setIssues(issuesData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  // Filter issues based on search term
  const filteredIssues = React.useMemo(() => {
    if (!issues) return [];
    
    return issues.filter(issue => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (issue.id.toString().includes(searchLower)) ||
        (issue.type?.toLowerCase().includes(searchLower)) ||
        (issue.rank?.toLowerCase().includes(searchLower)) ||
        (issue.line_name?.toLowerCase().includes(searchLower)) ||
        (issue.part_number?.toLowerCase().includes(searchLower)) ||
        (issue.part_name?.toLowerCase().includes(searchLower)) ||
        (issue.problem?.toLowerCase().includes(searchLower)) ||
        (issue.counter_measure?.toLowerCase().includes(searchLower)) ||
        (issue.final_counter_measure?.toLowerCase().includes(searchLower)) ||
        (issue.created_by_name?.toLowerCase().includes(searchLower)) ||
        (issue.pic_name?.toLowerCase().includes(searchLower)) ||
        (issue.status?.toLowerCase().includes(searchLower))
      );
    });
  }, [issues, searchTerm]);

  // Calculate progress based on status
  const getStatusPercentage = (status: string) => {
    switch (status) {
      case 'waiting_response': return 0.25;
      case 'inprogress': return 0.5;
      case 'waiting_approval': return 0.75;
      case 'completed': return 1;
      default: return 0;
    }
  };

  const formatWord = (word: string) => {
    return word
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Format date for display
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd-MMM-yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={200} />
        <Skeleton variant="text" sx={{ mt: 2 }} />
        <Skeleton variant="text" />
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">Error: {error}</Typography>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Project not found</Typography>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">
            {project.code}
          </Typography>
          <Chip 
            label={'Active'} 
            color={'success'}
            size="small"
            sx={{ ml: 2 }}
          />
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Event Details</Typography>
            <Typography variant="body1">{project.name || 'No name'}</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Lines</Typography>
                <Typography variant="body1">{project.lines_count || 0}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box>
                <Typography variant="subtitle2" color="text.secondary">Issues</Typography>
                <Typography variant="body1">{project.issues_count || 0}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Created On</Typography>
                <Typography variant="body1">
                  {project.created_on ? format(new Date(project.created_on), 'MMMM do, yyyy h:mm a') : ''}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>BIRA SUMMARY</Typography>
          <TextField
            placeholder="Search issues..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />
        </Box>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>No</TableCell>
                <TableCell>Type of Issue</TableCell>
                <TableCell>Rank</TableCell>
                <TableCell>Line</TableCell>
                <TableCell>Part No</TableCell>
                <TableCell>Part Name</TableCell>
                <TableCell>Problem Description</TableCell>
                <TableCell>Picture</TableCell>
                <TableCell>Counter Measure Request</TableCell>
                <TableCell>Final Counter Measure</TableCell>
                <TableCell>Issuer</TableCell>
                <TableCell>Issuer Date</TableCell>
                <TableCell>PIC</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredIssues.length > 0 ? (
                filteredIssues.map((issue) => (
                  <TableRow key={issue.id} hover>
                    <TableCell>{issue.id}</TableCell>
                    <TableCell>{issue.type || ''}</TableCell>
                    <TableCell>{issue.rank?.toUpperCase() || ''}</TableCell>
                    <TableCell>{issue.line_name || ''}</TableCell>
                    <TableCell>{issue.part_number || ''}</TableCell>
                    <TableCell>{issue.part_name || ''}</TableCell>
                    <TableCell>{issue.problem || ''}</TableCell>
                    <TableCell>
                      {issue.photos && issue.photos.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {issue.photos.slice(0, 2).map((photo, idx) => (
                            <Box
                              key={idx}
                              component="img"
                              src={getImageUrl(photo.file_name)}
                              alt={`Issue photo ${idx + 1}`}
                              sx={{ width: 50, height: 50, objectFit: 'cover' }}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{issue.counter_measure || ''}</TableCell>
                    <TableCell>{issue.final_counter_measure || ''}</TableCell>
                    <TableCell>{issue.created_by_name || ''}</TableCell>
                    <TableCell>{formatDate(issue.created_on)}</TableCell>
                    <TableCell>{issue.pic_name || ''}</TableCell>
                    <TableCell>{formatDate(issue.completion_date)}</TableCell>
                    <TableCell>
                      {formatWord(issue.status)}
                    </TableCell>
                    <TableCell>
                    <Box sx={{ position: 'relative', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {(() => {
                            const percentage = getStatusPercentage(issue.status);
                            let imageName = 'progress-25.png';
                            
                            if (percentage <= 0) {
                              // For 0% progress, just show the text without an image
                              return (
                                <Box 
                                  sx={{ 
                                    position: 'relative', 
                                    width: '40px', 
                                    height: '40px',
                                    border: '1px solid #ccc',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '10px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    0%
                                  </Typography>
                                </Box>
                              );
                            } else if (percentage <= 0.25) {
                              imageName = 'progress-25.png';
                            } else if (percentage <= 0.5) {
                              imageName = 'progress-50.png';
                            } else if (percentage <= 0.75) {
                              imageName = 'progress-75.png';
                            } else {
                              imageName = 'progress-100.png';
                            }
                            
                            return (
                              <Box sx={{ position: 'relative', width: '40px', height: '40px' }}>
                                <img 
                                  src={`${process.env.PUBLIC_URL}/${imageName}`} 
                                  alt={`${Math.round(percentage * 100)}% complete`}
                                  style={{ width: '100%', height: '100%' }}
                                />
                              </Box>
                            );
                          })()}
                        </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={16} align="center">
                    No issues found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
